import { useEffect } from 'react'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'
import { HoneycombWebSDK } from '@honeycombio/opentelemetry-web'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'

const configDefaults = {
  ignoreNetworkEvents: true,
}

declare global {
  interface Window {
    HONEYCOMB_CONFIG?: {
      apiKey?: string
    }
  }
}

const getRuntimeHoneycombApiKey = (): string | undefined => {
  if (typeof window !== 'undefined' && window.HONEYCOMB_CONFIG?.apiKey) {
    return window.HONEYCOMB_CONFIG.apiKey
  }

  return process.env.HONEYCOMB_API_KEY ?? undefined
}

const getSafeSampleRate = (rawSampleRate: unknown, defaultSampleRate = 1): number => {
  const numeric = typeof rawSampleRate === 'number' ? rawSampleRate : Number(rawSampleRate)

  if (!Number.isFinite(numeric) || numeric < 1) {
    return defaultSampleRate
  }

  return Math.floor(numeric)
}

export const HoneycombObservability = () => {
  const honeycombEnabled = useFeatureIsOn('honeycomb-web-observability')
  const sampleRateFeatureValue = useFeatureValue('honeycomb-web-sample-rate', 1)
  const apiKey = getRuntimeHoneycombApiKey()

  useEffect(() => {
    if (!honeycombEnabled || !apiKey) {
      return
    }

    try {
      const sdk = new HoneycombWebSDK({
        endpoint: 'https://api.eu1.honeycomb.io:443',
        debug: process.env.NODE_ENV === 'development',
        apiKey,
        serviceName: 'wcd-frontend',
        sampleRate: getSafeSampleRate(sampleRateFeatureValue, 1),
        instrumentations: [
          getWebAutoInstrumentations({
            // Loads custom configuration for xml-http-request instrumentation.
            '@opentelemetry/instrumentation-xml-http-request': configDefaults,
            '@opentelemetry/instrumentation-fetch': configDefaults,
            '@opentelemetry/instrumentation-document-load': configDefaults,
          }),
        ],
        resourceAttributes: {
          environment: process.env.NODE_ENV || 'production',
        },
      })
      sdk.start()
    } catch (e) {
      console.error('Failed to initialize Honeycomb:', e)
    }
  }, [honeycombEnabled, apiKey, sampleRateFeatureValue])

  return null
}
